import { Word } from '@/common/types/scopeAndSequence';
import { CreateAttempt } from '@/common/types/session';
import {
  playMakeTheWordAudio,
  playWordAudio,
  soundOutWord,
} from '@/common/utils/audio';
import { getDistractorGraphemes } from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  getWordGraphemes,
  getWordLevel,
} from '@/common/utils/scopeAndSequence';
import {
  segmentTypedAnswer,
  validateSegmentedAnswer,
} from '@/common/utils/segmentTypedAnswer';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getGeneralGameState,
  getSession,
  getStudent,
  getWordWeaver,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { MinigameController } from '@/game/GameComponents/PhaserGame/MinigameControllers/MinigameController';
import { WordWeaverCard } from '@/game/GameComponents/PhaserGame/Types/IMinigameCardData';
import {
  CardFeedback,
  KeyboardFeedback,
} from '@/game/GameComponents/PhaserGame/Types/MinigameTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { waitForSeconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { calculateProgress, shuffleItems } from '@/game/gameHelpers';
import SingleCharacterText from '@/game/prefabs/StudentGame/SingleCharacterText';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import Minigame from '@/game/scenes/Minigame';
import { v4 as uuidv4 } from 'uuid';

export class WordWeaverController extends MinigameController {
  get state() {
    return GameInstance.instance.wordWeaver;
  }

  override startRound() {
    const isRoundEnded = getGeneralGameState().isRoundEnded;
    if (isRoundEnded) {
      GameInstance.instance.updateGeneralGameState({
        isRoundEnded: false,
      });
    }

    this.handleSetWordToBuild();

    GameInstance.instance.updateWordWeaver({
      wordsServed: getWordWeaver().wordsServed + 1,
    });
  }

  async handleSetWordToBuild() {
    const gameData = getGeneralGameState().gameData;
    const currentWordList = gameData.wordWeaver;

    const filteredWordList: Word[] = (currentWordList || []).filter(
      (word) =>
        !getWordWeaver().usedWords.some(
          (usedWord) => usedWord.spelling === word.spelling,
        ),
    );

    const wordToBuild =
      filteredWordList[Math.floor(Math.random() * filteredWordList.length)];
    GameInstance.instance.updateWordWeaver({
      wordToBuild,
    });
    if (!wordToBuild) {
      console.error('No word to build found');
      return;
    }

    const level = GameInstance.instance.getShouldPlayCustomLevel()
      ? getWordLevel(wordToBuild.id)
      : getStudent().level;
    const distractorCardsEnabled =
      getStudent().settings.distractorCardsEnabled ?? true;

    const wordGraphemes = getWordGraphemes(wordToBuild);
    const DISTRACTOR_COUNT = 3;
    const distractors = distractorCardsEnabled
      ? getDistractorGraphemes(wordGraphemes, level, DISTRACTOR_COUNT)
      : [];

    // Combine word graphemes and distractor graphemes
    const allGraphemes = [...wordGraphemes, ...distractors];

    // Create shuffled array of all phonemes with unique IDs
    const shuffledWordObjects = shuffleItems(allGraphemes).map((grapheme) => ({
      id: uuidv4(),
      grapheme,
    }));

    GameInstance.instance.updateWordWeaver({
      initialShuffledWord: shuffledWordObjects,
      attemptLetterCount: wordGraphemes.length,
    });
  }

  override async checkMinigameAnswer() {
    const student = getStudent();
    if (!student.id) {
      console.error('No student');
      return;
    }

    const studentAttempt = this.minigame.getInput().trim().toLowerCase();
    if (!studentAttempt.length) {
      console.error('No student attempt');
      return;
    }

    const wordToBuild = getWordWeaver().wordToBuild;
    if (!wordToBuild) {
      console.error('No word to build found');
      return;
    }

    const currentActivityId = getSession().currentActivity?.id;

    if (!currentActivityId) {
      console.error('No current activity ID found');
      return;
    }

    // Increment wordAttempts
    GameInstance.instance.updateWordWeaver({
      wordAttempts: getWordWeaver().wordAttempts + 1,
    });

    // Add attempt data
    const attemptData: CreateAttempt = {
      organisationId: student.organisationId,
      studentId: student.id,
      activityId: currentActivityId,
      activityName: 'wordweaver',
      studentAnswer: studentAttempt,
      cardsPlayed: this.minigame.getBoardCards().map((card) => card.cardText),
      correctAnswer: wordToBuild.spelling,
      correctAnswerId: wordToBuild.id,
      control: this.minigame.getControlScheme(student.settings.controllerType),
      startedAt: getSession().currentAttemptStartedAt,
      endedAt: Date.now(),
    };
    GameInstance.instance.addAttempt(attemptData);

    const isCorrect = studentAttempt === wordToBuild.spelling;

    // update accuracy
    GameInstance.instance.updateActivityAccuracy(isCorrect);

    if (isCorrect) {
      GameInstance.instance.updateWordWeaver({
        usedWords: [...getWordWeaver().usedWords, wordToBuild],
        wordsGuessedCorrectly: getWordWeaver().wordsGuessedCorrectly + 1,
        totalCorrectLettersGuessed:
          getWordWeaver().totalCorrectLettersGuessed + studentAttempt.length,
      });

      await this.showFeedback(this.getItemFeedback(), {
        correctAnswer: true,
      });
    } else {
      // Word is incorrect
      GameInstance.instance.updateWordWeaver({
        wordsServed: getWordWeaver().wordsServed + 1,
      });

      await this.showFeedback(this.getItemFeedback(), {
        correctAnswer: false,
        partiallyCorrectAnswer:
          this.getIsPartiallyCorrectAnswer(studentAttempt),
        forceIndividualFeedback: true,
        forceReadout: true,
      });
    }

    // Calculate and update progress
    const newProgress = calculateProgress(
      getGeneralGameState(),
      getWordWeaver(),
      100,
    );
    GameInstance.instance.updateGeneralGameState({
      progress: newProgress,
    });

    if (isCorrect) {
      EventBus.emit(GAME_EVENTS.MINIGAME_CORRECT_ANSWER, attemptData);
    } else {
      EventBus.emit(GAME_EVENTS.MINIGAME_INCORRECT_ANSWER, attemptData);
    }
  }

  // This can also create card slots.
  override createCardList(minigameScene: Minigame): {
    dealtCards: StudentGameCard[];
    boardCards: StudentGameCard[];
  } {
    return {
      dealtCards: getWordWeaver().initialShuffledWord.map(
        (card: WordWeaverCard) => {
          const newCard = new StudentGameCard(minigameScene, 0, 0);
          minigameScene.initializeNewMinigameCard(newCard, card, 'hand');
          return newCard;
        },
      ),
      boardCards: [],
    };
  }

  override getMaxBoardCards(): number {
    return getWordWeaver().attemptLetterCount;
  }

  override getShouldDisplayBoardSlots(): boolean {
    return true;
  }

  override shouldFillBoardWithSlots(): boolean {
    return true;
  }

  override async playAudioForRound() {
    const wordToBuild = getWordWeaver().wordToBuild;
    const audioDelay = getStudent().settings.audio.audioDelay;
    const wordSegmentationEnabled =
      getStudent().settings?.wordSegmentationEnabled ?? false;
    if (!wordToBuild) {
      console.error('No word to build found');
      return;
    }

    //only play "make the word" instruction on the first word
    if (getWordWeaver().wordsServed === 1) {
      await playMakeTheWordAudio();
    }

    const wordGraphemes = getWordGraphemes(wordToBuild);
    await playWordAudio(wordToBuild.spelling);
    if (wordSegmentationEnabled) {
      await soundOutWord(
        Object.values(wordGraphemes),
        audioDelay,
        wordGraphemes,
        this.handleSegmentationAudioPlayed.bind(this),
      );
      await playWordAudio(wordToBuild.spelling);
    }
  }

  override async playInstructions() {
    const wordToBuild = getWordWeaver().wordToBuild;
    if (!wordToBuild) {
      console.error('No word to build found');
      return;
    }

    const student = getStudent();
    const studentId = student.id;
    if (studentId) {
      trackMixpanelEvent('Replay word button clicked', {
        word: wordToBuild.spelling,
      });
    }

    await this.soundOutWord(wordToBuild);
  }

  override getCardFeedback(): CardFeedback[] {
    const boardState = this.minigame.getBoardDropSite().layoutGroup;
    if (!boardState) {
      console.error('No board state found');
      return [];
    }
    const wordToBuild = getWordWeaver().wordToBuild;
    if (!wordToBuild) return [];

    const boardCards = boardState.getMinigameCards();
    const graphemesForWord = getWordGraphemes(wordToBuild);

    return boardCards.map((card: StudentGameCard, i: number) => {
      const thisGrapheme = graphemesForWord[i];

      return {
        card,
        isCorrect: thisGrapheme.spelling === card.cardData?.grapheme?.spelling,
        grapheme: thisGrapheme,
      };
    });
  }

  override getKeyboardFeedback(): KeyboardFeedback[] {
    const studentAttempt = this.minigame.getTypedInput();
    if (!studentAttempt) return [];

    const word = getWordWeaver().wordToBuild!;
    const graphemes = getWordGraphemes(word);

    let feedback: KeyboardFeedback[] = [];
    let letterIndex = 0;

    // Validation:
    // See if the student's graphemes are in the same order as the word's graphemes.
    const graphemeSpellings = graphemes.map((grapheme) => grapheme.spelling);
    const studentAnswer = segmentTypedAnswer(studentAttempt, graphemeSpellings);
    const validation = validateSegmentedAnswer(
      studentAnswer,
      graphemeSpellings,
    );

    for (let i = 0; i < validation.length; i++) {
      const studentGrapheme = studentAnswer[i];
      const isCorrect = validation[i];
      const letterEnd = letterIndex + studentGrapheme.length;
      const letters = this.minigame
        .getPlayerTypingInputs()[0]
        .getCharacterArray()
        .slice(letterIndex, letterEnd) as SingleCharacterText[];
      letterIndex += studentGrapheme.length;
      feedback.push({
        letters,
        isCorrect,
      });
    }
    return feedback;
  }

  override async readFinalAnswer() {
    const wordToCreate = getWordWeaver().wordToBuild;
    if (!wordToCreate) return;
    await playWordAudio(wordToCreate.spelling);
  }

  getIsPartiallyCorrectAnswer(studentAttempt: string) {
    const wordToBuild = getWordWeaver().wordToBuild;
    if (!wordToBuild) {
      console.error('No word to build found');
      return false;
    }
    const attemptInWord = wordToBuild.spelling
      .toLowerCase()
      .includes(studentAttempt.toLowerCase());
    const attemptAtStart = wordToBuild.spelling.indexOf(studentAttempt) === 0;
    return attemptInWord && attemptAtStart;
  }

  override async handleIncorrectFeedbackComplete(): Promise<void> {
    await waitForSeconds(this.minigame.time, 0.25);
    await this.playInstructions();
  }

  override canShowArticulatoryGestures(): boolean {
    return true;
  }
}
